import React, { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { Types } from "@sendum/core";

import { PalletSelectorRootState } from "../store/PalletSelectorStore";

import s from "./PalletsList.module.scss";
import { Pallet } from "./Pallet";
import { refreshPallets, setPallet } from "../store/PalletSelectorActions";
import { IMoveModalRef, MoveModal } from "./MoveModal";
import { AnyAction } from "redux";
import { ConfirmSetDriver, IConfirmSetDriverRef } from "./ConfirmSetDriverModal";

interface IProps {
	className?: string;
	isMovement?: boolean;
}

export const PalletsList = ({ className, isMovement }: IProps): JSX.Element => {
	const dispatch = useDispatch();
	const pallets = useSelector<PalletSelectorRootState, Types.Pallet[]>((state: PalletSelectorRootState) => state.PalletSelector.pallets, shallowEqual);
	const pallet = useSelector<PalletSelectorRootState, Types.Pallet | undefined>((state: PalletSelectorRootState) => state.PalletSelector.pallet, shallowEqual);
	const account = useSelector<PalletSelectorRootState, Types.Account | undefined>((state: PalletSelectorRootState) => state.PalletSelector.account, shallowEqual);
	const moveModalRef = useRef<IMoveModalRef>(null);
	const [isShowHeaderShadow, setShowHeaderShadow] = useState<boolean>(false);
	const [isShowFooterShadow, setShowFooterShadow] = useState<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const confirmModalRef = useRef<IConfirmSetDriverRef>(null);
	const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleOnClickRow = useCallback((item: Types.Pallet) => {
		if (timer.current) {
			clearTimeout(timer.current);
			timer.current = null;
		}

		timer.current = setTimeout(() => {
			if (timer.current) {
				clearTimeout(timer.current);
				timer.current = null;
			}

			dispatch(setPallet(item));

			if (item && !!account && account.Role !== Types.AccountRole.VIEW_ONLY_USER && isMovement) {
				if ((item.AssignedDriver?.UserName !== undefined && item.AssignedDriver?.UserName === account?.UserName) || account?.Role === Types.AccountRole.ADMIN) {
					moveModalRef.current?.open(item);
				} else {
					confirmModalRef.current?.open(item);
				}
			}
		},300);
	}, [isMovement, account, dispatch]);

	const onScrollHandler = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		setShowHeaderShadow(event.currentTarget.scrollTop >= 1);
		setShowFooterShadow(event.currentTarget.scrollHeight - event.currentTarget.offsetHeight - event.currentTarget.scrollTop >= 1);
	}, []);

	const onMoved = useCallback(() => {
		dispatch(refreshPallets(isMovement) as unknown as AnyAction);
	}, [dispatch, isMovement]);

	useEffect(() => {
		if (containerRef.current) {
			setShowHeaderShadow(containerRef.current.scrollTop >= 1);
			setShowFooterShadow(containerRef.current.scrollHeight - containerRef.current.offsetHeight - containerRef.current.scrollTop >= 1);
		}
	}, [pallets]);

	const onConfirmed = useCallback((palletId: string) => {
		dispatch(refreshPallets(isMovement) as unknown as AnyAction);
		const p = pallets.find(_ => _.PalletId === palletId);
		if (p) {
			moveModalRef.current?.open(p);
		}
	},[dispatch, pallets, isMovement]);

	return (
		<div className={`${className ?? ""} ${s.PalletsList} `}>
			<div
				className={`${s.PalletsList__header} ${isShowHeaderShadow ? s.PalletsList__shadowHeader : s.PalletsList__normalHeader}`}/>
			<div className={s.PalletsList__container} onScroll={onScrollHandler} ref={containerRef}>
				{pallets.map(p => (<Pallet isViewOnly={isMovement && !!account && account.Role === Types.AccountRole.VIEW_ONLY_USER} key={p.PalletId} pallet={p} onClick={handleOnClickRow}
					isActive={p.PalletId === pallet?.PalletId}/>))}
			</div>
			<div
				className={`${s.PalletsList__footer} ${isShowFooterShadow ? s.PalletsList__shadowFooter : s.PalletsList__normalFooter}`}/>
			<MoveModal ref={moveModalRef} onMoved={onMoved} isMovement={isMovement}/>
			<ConfirmSetDriver ref={confirmModalRef} onConfirmed={onConfirmed}/>
		</div>
	);
};
